<template>
  <div class="keyboard-container">
    <div v-for="(row, rowIndex) in keyboardRows" :key="rowIndex" class="keyboard-grid">
      <div v-for="letter in row" :key="letter" class="letter-container word-grid">
        <v-btn
          @click="$emit('letterClick', letter)"
          color="on-surface"
          :disabled="disabled || guessedLetters.has(letter)"
          :class="getLetterStyles(letter)"
          class="letter-button"
          variant="text"
          size="xs"
        >
          {{ letter }}

          <!-- Strike-through for incorrect letters -->
          <div v-if="getLetterStatus(letter) === 'incorrect'" class="strike-through" />
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SWEDISH_ALPHABET_LOWERCASE } from '../data/swedishAlphabet';
import { computed, onMounted } from 'vue';

interface Props {
  guessedLetters: Set<string>;
  currentWord: string;
  disabled: boolean;
}

const props = defineProps<Props>();

// Split the alphabet into two roughly equal rows (15 / 14) so the keyboard
// stays balanced instead of greedily filling the first row.
const keyboardRows = computed(() => {
  const half = Math.ceil(SWEDISH_ALPHABET_LOWERCASE.length / 2);
  return [SWEDISH_ALPHABET_LOWERCASE.slice(0, half), SWEDISH_ALPHABET_LOWERCASE.slice(half)];
});

const emits = defineEmits<{
  letterClick: [letter: string];
}>();

const getLetterStatus = (letter: string) => {
  if (!props.guessedLetters.has(letter)) return 'unused';
  return props.currentWord.includes(letter) ? 'correct' : 'incorrect';
};

const getLetterStyles = (letter: string) => {
  const status = getLetterStatus(letter);

  switch (status) {
    case 'correct':
      return 'letter-correct';
    case 'incorrect':
      return 'letter-incorrect';
    default:
      return 'letter-unused';
  }
};

const handleKeyPress = (event: any) => {
  if (
    SWEDISH_ALPHABET_LOWERCASE.includes(event.key.toLowerCase()) &&
    !props.guessedLetters.has(event.key.toLowerCase())
  ) {
    emits('letterClick', event.key.toLowerCase());
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeyPress);
});
</script>

<style scoped>
.word-container {
  width: 100%;
  margin: 0 auto;
}

.word-grid {
  display: grid;
  gap: 16px;
  justify-items: center;
}

.keyboard-container {
  width: 100%;
  max-width: calc(min(80%, 1200px));
  margin: 0 auto;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.8vh, 12px);
}

.keyboard-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: clamp(4px, 0.6vw, 8px);
  justify-content: center;
}

.letter-button {
  font-family: 'Chalkduster', sans-serif;
  font-size: clamp(1.5rem, 2.6vw, 4rem) !important;
  font-weight: bold !important;
  user-select: none;
  min-width: unset !important;
  padding: clamp(4px, 0.6vw, 8px) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.letter-unused {
  opacity: 0.9;
}

.letter-unused:hover {
  opacity: 1 !important;
}

.letter-correct {
  opacity: 0;
  cursor: not-allowed !important;
}

.letter-incorrect {
  opacity: 0.3;
  cursor: not-allowed !important;
}

.strike-through {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  opacity: 1 !important;
  background: linear-gradient(90deg, transparent 0%, currentColor 30%, currentColor 80%, transparent 100%) !important;
  transform: translateY(-50%) rotate(12deg);
  filter: blur(0.3px);
  pointer-events: none;
}
</style>
